import { useCallback, useEffect, useRef, useState } from "react";

import type { ClientSignals } from "~/lib/antibot/types";

/**
 * Señales de "esto lo completó una persona" para el pedido de código. NUNCA bloquean en el
 * cliente: sólo informan, y la API decide (con el nivel con clic, no con un rechazo).
 *
 * - `interactions`: eventos reales de la persona (`isTrusted`): toques, clics, teclas, texto que
 *   entra. El clic cuenta aparte del toque porque un lector de pantalla (VoiceOver) activa con un
 *   `click` sin `pointerdown`. El foco NO cuenta: `element.focus()` desde código también genera un
 *   foco "confiable", y los formularios que enfocan solos el teléfono al abrir le regalarían una
 *   interacción a cualquier script.
 * - `fillMs`: desde la primera interacción real hasta el envío. 0 si no hubo ninguna.
 * - `webdriver`: el navegador declara que lo maneja un automatizador (`navigator.webdriver`).
 * - `honeypot`: lo que haya en el campo trampa (`<BotTrap />`). Una persona no lo ve, así que
 *   siempre llega vacío.
 *
 * Se escucha en todo el documento mientras el formulario está montado: cualquier interacción con
 * la página cuenta como "hay alguien", que es lo único que interesa.
 */

export type FormSignals = Pick<ClientSignals, "fillMs" | "interactions" | "webdriver" | "honeypot">;

/** Tope del contador: es una señal, no hace falta contar más que esto. */
const MAX_INTERACTIONS = 10_000;
const HONEYPOT_MAX = 200;

export const INTERACTION_EVENTS = ["pointerdown", "click", "keydown", "input"] as const;

/** El contador, separado de React y del DOM para poder probarlo con eventos "confiables". */
export function createSignalsTracker(now: () => number = Date.now) {
  let firstAt: number | null = null;
  let count = 0;

  return {
    observe(event: { isTrusted: boolean }) {
      if (!event.isTrusted) return;
      if (firstAt === null) firstAt = now();
      if (count < MAX_INTERACTIONS) count += 1;
    },
    read(): Pick<FormSignals, "fillMs" | "interactions"> {
      return {
        fillMs: firstAt === null ? 0 : Math.max(0, now() - firstAt),
        interactions: count,
      };
    },
  };
}

function isWebdriver() {
  try {
    return typeof navigator !== "undefined" && navigator.webdriver === true;
  } catch {
    return false;
  }
}

export function useFormSignals() {
  const [tracker] = useState(() => createSignalsTracker());
  const trapRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onEvent = (event: Event) => tracker.observe(event);
    // Captura: se cuenta aunque un componente frene la propagación.
    for (const type of INTERACTION_EVENTS) document.addEventListener(type, onEvent, true);
    return () => {
      for (const type of INTERACTION_EVENTS) document.removeEventListener(type, onEvent, true);
    };
  }, [tracker]);

  /** Lo que va en `clientSignals` (el resto lo completa el antibot). */
  const read = useCallback((): FormSignals => {
    const signals: FormSignals = { ...tracker.read(), webdriver: isWebdriver() };
    const trap = trapRef.current?.value ?? "";
    if (trap) signals.honeypot = trap.slice(0, HONEYPOT_MAX);
    return signals;
  }, [tracker]);

  return { trapRef, read };
}
